import React from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { Terminal, Gift, Sparkles, ShoppingBag } from 'lucide-react'

const HeroSection: React.FC = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.4,
      },
    },
  }

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 1.2,
        ease: 'easeOut',
      },
    },
  }

  const iconVariants = {
    hidden: { scale: 0.8, opacity: 0 },
    visible: {
      scale: 1,
      opacity: 1,
      transition: {
        duration: 1.5,
        ease: 'easeOut',
      },
    },
  }

  return (
    <section className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 via-white to-gray-100 dark:from-secondary-700 dark:via-secondary-800 dark:to-secondary-900 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(254,66,21,0.05),transparent_50%)] dark:bg-[radial-gradient(circle_at_30%_50%,rgba(254,66,21,0.1),transparent_50%)] pointer-events-none"></div>
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_20%,rgba(35,61,113,0.05),transparent_50%)] dark:bg-[radial-gradient(circle_at_80%_20%,rgba(35,61,113,0.3),transparent_50%)] pointer-events-none"></div>
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="text-center"
        >
          {/* Logo Icon */}
          <motion.div
            variants={iconVariants}
            className="flex justify-center mb-12"
          >
            <div className="w-32 h-32 md:w-40 md:h-40 lg:w-48 lg:h-48 flex items-center justify-center">
              <img 
                src="/assets/images/logo-icon.png" 
                alt="MomentScience Logo" 
                className="w-full h-full object-contain drop-shadow-2xl"
              />
            </div>
          </motion.div>

          {/* Main Title */}
          <motion.h1
            variants={itemVariants}
            className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight"
          >
            <span className="bg-gradient-to-r from-primary-400 via-primary-500 to-primary-600 bg-clip-text text-transparent">
              MomentScience
            </span>
            <br />
            <span className="text-gray-700 dark:text-white font-light">Labs</span>
          </motion.h1>

          {/* Action Buttons */}
          <motion.div
            variants={itemVariants}
            className="mt-8 flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4"
          >
            <Link
              to="/api-explorer"
              className="inline-flex items-center space-x-3 px-8 py-4 bg-[#FE4215] text-white rounded-lg hover:bg-[#E53910] transition-all duration-300 font-medium text-lg font-sans"
              style={{ 
                borderRadius: '8px',
                fontFamily: 'Inter, sans-serif'
              }}
            >
              <Terminal className="w-6 h-6" />
              <span>Explore APIs</span>
            </Link>
            
            <Link
              to="/usp-setup"
              className="inline-flex items-center space-x-3 px-8 py-4 bg-[#233D71] text-white rounded-lg hover:bg-[#1A2B49] transition-all duration-300 font-medium text-lg font-sans"
              style={{ 
                borderRadius: '8px',
                fontFamily: 'Inter, sans-serif'
              }}
            >
              <Gift className="w-6 h-6" />
              <span>USP Demo</span>
            </Link>

            <Link
              to="/moment-perks"
              className="inline-flex items-center space-x-3 px-8 py-4 bg-[#3B82F6] text-white rounded-lg hover:bg-[#2563EB] transition-all duration-300 font-medium text-lg font-sans"
              style={{ 
                borderRadius: '8px',
                fontFamily: 'Inter, sans-serif'
              }}
            >
              <Sparkles className="w-6 h-6" />
              <span>MomentPerks</span>
            </Link>

            <Link
              to="/shopify-demo"
              className="inline-flex items-center space-x-3 px-8 py-4 bg-[#95BF47] text-white rounded-lg hover:bg-[#7EA438] transition-all duration-300 font-medium text-lg font-sans"
              style={{
                borderRadius: '8px',
                fontFamily: 'Inter, sans-serif'
              }}
            >
              <ShoppingBag className="w-6 h-6" />
              <span>Shopify Demo</span>
            </Link>
          </motion.div>
        </motion.div>
      </div>
    </section>
  )
}

export default HeroSection
